import { useState, useEffect, useCallback } from 'react';

const cache = {
  favoritedCampaigns: { data: [], fetchedAt: 0 },
  favoritedIds: { data: [], fetchedAt: 0 },
};

let inFlightFavorites = null;
let inFlightFavoriteIds = null;

// TTL: 30 seconds
const FAVORITES_TTL_MS = 30000;

// Listeners: any mounted hook instance registers a forceUpdate here
const listeners = new Set();
function notifyListeners() {
  listeners.forEach((fn) => fn());
}

async function fetchFavoritesData(force = false) {
  const now = Date.now();
  if (!force && cache.favoritedCampaigns.fetchedAt && now - cache.favoritedCampaigns.fetchedAt < FAVORITES_TTL_MS) {
    return cache.favoritedCampaigns.data;
  }

  // Dedupe: if a request is already in-flight, wait for it
  if (inFlightFavorites) {
    return inFlightFavorites;
  }

  const promise = (async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/campaigns/favorites`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        cache.favoritedCampaigns = { data, fetchedAt: Date.now() };
        const ids = Array.isArray(data)
          ? data.map((fav) => fav.campaign_id).filter((id) => id != null)
          : [];
        cache.favoritedIds = { data: ids, fetchedAt: Date.now() };
        return data;
      }
      throw new Error('Failed to fetch favorited campaigns');
    } finally {
      inFlightFavorites = null;
    }
  })();

  inFlightFavorites = promise;
  return promise;
}

async function fetchFavoriteIds(force = false) {
  const now = Date.now();
  if (!force && cache.favoritedIds.fetchedAt && now - cache.favoritedIds.fetchedAt < FAVORITES_TTL_MS) {
    return cache.favoritedIds.data;
  }

  if (inFlightFavoriteIds) {
    return inFlightFavoriteIds;
  }

  // Prefer full-list cache when present (dashboard already loaded details)
  if (
    !force
    && cache.favoritedCampaigns.fetchedAt
    && now - cache.favoritedCampaigns.fetchedAt < FAVORITES_TTL_MS
  ) {
    const ids = cache.favoritedCampaigns.data
      .map((fav) => fav.campaign_id)
      .filter((id) => id != null);
    cache.favoritedIds = { data: ids, fetchedAt: cache.favoritedCampaigns.fetchedAt };
    return ids;
  }

  const promise = (async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/campaigns/favorites/ids`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const ids = Array.isArray(data) ? data : [];
        cache.favoritedIds = { data: ids, fetchedAt: Date.now() };
        return ids;
      }
      throw new Error('Failed to fetch favorited campaign ids');
    } finally {
      inFlightFavoriteIds = null;
    }
  })();

  inFlightFavoriteIds = promise;
  return promise;
}

/**
 * Shared hook for favorited campaigns data.
 * Uses module-level cache so data is shared across all mounted components.
 * Maintains backward compatibility by returning a Set of campaign IDs.
 *
 * @param {boolean} isAuthenticated - Whether the user is authenticated
 * @param {string} userType - The user type (e.g., 'influencer')
 * @param {{ idsOnly?: boolean }} [options] - When idsOnly, fetch /favorites/ids (landing hearts)
 * @returns {{ favoritedCampaigns: Set, favoritedCampaignsData: Array, loading: boolean, error: string|null, refresh: Function, setFavoritedCampaigns: Function, lastUpdated: number }}
 */
/** Resets module cache (for tests and rare logout flows). */
export function resetFavoritedCampaignsCache() {
  cache.favoritedCampaigns = { data: [], fetchedAt: 0 };
  cache.favoritedIds = { data: [], fetchedAt: 0 };
  inFlightFavorites = null;
  inFlightFavoriteIds = null;
}

/** Shared favorited-campaigns fetch for dashboard omnibar indexing. */
export async function fetchFavoritedCampaignsForOmnibar(force = false) {
  return fetchFavoritesData(force);
}

export default function useFavoritedCampaigns(isAuthenticated, userType, options = {}) {
  const { idsOnly = false } = options;
  const [favoritedCampaignsData, setFavoritedCampaignsData] = useState(
    idsOnly ? [] : cache.favoritedCampaigns.data,
  );
  const [favoritedCampaigns, setFavoritedCampaigns] = useState(
    () => new Set(cache.favoritedIds.data),
  );
  const [loading, setLoading] = useState(
    idsOnly ? !cache.favoritedIds.fetchedAt : !cache.favoritedCampaigns.fetchedAt,
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    if (idsOnly) return;
    const favoritedIds = new Set(favoritedCampaignsData.map((fav) => fav.campaign_id));
    setFavoritedCampaigns(favoritedIds);
  }, [favoritedCampaignsData, idsOnly]);

  // Sync local state from module cache
  const syncFromCache = useCallback(() => {
    if (idsOnly) {
      setFavoritedCampaigns(new Set(cache.favoritedIds.data));
      return;
    }
    setFavoritedCampaignsData(cache.favoritedCampaigns.data);
  }, [idsOnly]);

  // Register this instance as a listener
  useEffect(() => {
    listeners.add(syncFromCache);
    return () => {
      listeners.delete(syncFromCache);
    };
  }, [syncFromCache]);

  // Refresh function callable by consumers
  const refresh = useCallback(async (force = false) => {
    if (!isAuthenticated || userType !== 'influencer') return;
    try {
      setError(null);
      if (idsOnly) {
        const ids = await fetchFavoriteIds(force);
        setFavoritedCampaigns(new Set(ids));
        notifyListeners();
        return;
      }
      const data = await fetchFavoritesData(force);
      setFavoritedCampaignsData(data);
      notifyListeners();
    } catch (err) {
      setError(err.message || 'Failed to load favorited campaigns.');
    }
  }, [isAuthenticated, userType, idsOnly]);

  // Initial fetch on mount
  useEffect(() => {
    if (!isAuthenticated || userType !== 'influencer') {
      return;
    }

    let cancelled = false;

    const loadData = async () => {
      if (idsOnly) {
        if (cache.favoritedIds.fetchedAt) {
          setFavoritedCampaigns(new Set(cache.favoritedIds.data));
          setLoading(false);

          const now = Date.now();
          if (now - cache.favoritedIds.fetchedAt >= FAVORITES_TTL_MS) {
            try {
              await refresh(false);
            } catch {
              // Ignore background refresh errors
            }
          }
          return;
        }

        setLoading(true);
        try {
          const ids = await fetchFavoriteIds(false);
          if (!cancelled) {
            setFavoritedCampaigns(new Set(ids));
            notifyListeners();
          }
        } catch (err) {
          if (!cancelled) {
            setError(err.message || 'Failed to load favorited campaigns.');
            console.error('Error fetching favorited campaign ids:', err);
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
        return;
      }

      if (cache.favoritedCampaigns.fetchedAt) {
        setFavoritedCampaignsData(cache.favoritedCampaigns.data);
        setLoading(false);

        // Refresh in background if stale
        const now = Date.now();
        if (now - cache.favoritedCampaigns.fetchedAt >= FAVORITES_TTL_MS) {
          try {
            await refresh(false);
          } catch {
            // Ignore background refresh errors
          }
        }
        return;
      }

      setLoading(true);
      try {
        const data = await fetchFavoritesData(false);
        if (!cancelled) {
          setFavoritedCampaignsData(data);
          notifyListeners();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load favorited campaigns.');
          console.error('Error fetching favorited campaigns:', err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => { // eslint-disable-line consistent-return
      cancelled = true;
    };
  }, [isAuthenticated, userType, idsOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    favoritedCampaigns,
    setFavoritedCampaigns,
    favoritedCampaignsData,
    loading,
    error,
    refresh,
    lastUpdated: idsOnly ? cache.favoritedIds.fetchedAt : cache.favoritedCampaigns.fetchedAt,
  };
}
